/** Month arithmetic and formatting for the calendar's navigation. */

export interface MonthRef {
  year: number;
  /** 1-12, not the JavaScript 0-11, so it matches the YYYY-MM wire format. */
  month: number;
}

export function toMonthString({ year, month }: MonthRef): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}

export function currentMonth(): MonthRef {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

export function addMonths(ref: MonthRef, delta: number): MonthRef {
  const zeroBased = ref.month - 1 + delta;
  return {
    year: ref.year + Math.floor(zeroBased / 12),
    month: ((zeroBased % 12) + 12) % 12 + 1,
  };
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function monthLabel({ year, month }: MonthRef): string {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

export const MONTH_OPTIONS = MONTH_NAMES.map((name, index) => ({
  value: index + 1,
  label: name,
}));

/**
 * Weekday index with Monday as 0.
 *
 * The calendar is Monday-first because the team's working week is, and because the spec's
 * screenshots show weekends at the end of the row.
 */
export function mondayFirstWeekday(date: Date): number {
  return (date.getDay() + 6) % 7;
}

export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Parses "YYYY-MM-DD" without going through Date's timezone handling. */
export function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function formatDayLabel(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
